import strings from 'strings'
import {
  ACCENT_ORANGE,
  ACCENT_PURPLE,
  BANNER_BG,
  GRAY_BAR,
  STATUS_STYLES,
  formatCurrency,
  formatMultiple,
  formatPercent,
  formatPeriodLabel,
  formatPeriodLabelLong,
  formatSignedCurrency,
  formatSignedPercent,
  getHealthStatusLabel,
  getStatusStyle,
  splitFlag
} from '../BalanceSheet/balanceSheetAdapters'

// Formatters, status tiers and palette are shared with the Balance Sheet tab; only the metrics differ.
export {
  ACCENT_ORANGE,
  ACCENT_PURPLE,
  BANNER_BG,
  GRAY_BAR,
  STATUS_STYLES,
  formatCurrency,
  formatMultiple,
  formatPercent,
  formatPeriodLabel,
  formatPeriodLabelLong,
  formatSignedCurrency,
  formatSignedPercent,
  getHealthStatusLabel,
  getStatusStyle,
  splitFlag
}

export const THRESHOLDS = {
  GROSS_MARGIN: 30,
  OPERATING_MARGIN: 10,
  NET_MARGIN_THIN: 5,
  INTEREST_COVERAGE_FLOOR: 1.5,
  INTEREST_COVERAGE_COMFORT: 3,
  CONSISTENCY_TOLERANCE: 1
}

export const NOT_REPORTED = '—'

const num = (value) => (Number.isFinite(Number(value)) ? Number(value) : 0)

// Document Insights leaves a figure null when the statement does not print it; null must stay distinguishable from 0.
export const isReported = (value) => value !== null && value !== undefined && Number.isFinite(Number(value))

const optional = (value) => (isReported(value) ? Number(value) : null)

export const formatOptionalCurrency = (value) => (isReported(value) ? formatCurrency(value) : NOT_REPORTED)

export const formatOptionalPercent = (value, digits = 1) => (isReported(value) ? formatPercent(value, digits) : NOT_REPORTED)

export const formatOptionalMultiple = (value) => (isReported(value) ? formatMultiple(value) : NOT_REPORTED)

// A share of revenue that rounds to zero is printed as "0.0%", never as "-0.0%".
export const formatShare = (value, digits = 1) => {
  const rounded = Number(num(value).toFixed(digits))
  return formatPercent(rounded === 0 ? 0 : value, digits)
}

export const formatOptionalShare = (value, digits = 1) => (isReported(value) ? formatShare(value, digits) : NOT_REPORTED)

export const formatPoints = (value, digits = 1) => `${num(value) >= 0 ? '+' : ''}${num(value).toFixed(digits)} ${strings.INCOME_STATEMENT_POINTS}`

// Period bounds come as plain yyyy-MM-dd; parsed as a local date so the day does not shift with the timezone.
export const formatDayLabel = (dateStr) => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateStr || '')
  if (!match) return formatPeriodLabel(dateStr)
  const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]))
  return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
}

export const formatPeriodRange = (periodStart, periodEnd) => {
  if (periodStart && periodEnd) return `${formatDayLabel(periodStart)} – ${formatDayLabel(periodEnd)}`
  return formatDayLabel(periodEnd || periodStart)
}

export const readMetrics = (doc) => {
  const insights = doc?.insights || {}
  const profitability = insights.profitability || {}
  const cashEarnings = insights.cashEarnings || {}
  const debtService = insights.debtService || {}
  const costStructure = insights.costStructure || {}

  const operatingIncome = optional(profitability.operatingIncome)
  const netIncome = optional(profitability.netIncome)

  return {
    date: doc?.date || '',
    periodStart: doc?.periodStart || '',
    periodEnd: doc?.periodEnd || doc?.date || '',
    error: doc?.error || null,
    warnings: doc?.warnings || [],
    // Mismatches only one source could flag: extraction doubts, never facts about the business
    extractionDoubts: doc?.extractionDoubts || [],
    confidence: doc?.confidence || null,
    documentNotes: doc?.documentNotes || [],
    sourceLines: doc?.sourceLines || {},
    validIncomeStatement: doc?.validIncomeStatement !== false,
    // What Document Insights recognized the file as (BALANCE_SHEET, CASH_FLOW_STATEMENT...) when it rejected it
    documentType: doc?.documentType || null,
    pageCount: isReported(doc?.pageCount) ? Number(doc.pageCount) : null,
    // False when rows, pages or lines of the document were never captured: the bottom line is unverified
    statementComplete: doc?.statementComplete !== false,
    // How many checks verified against printed figures and the chip label; null on older documents
    checksSummary: doc?.checksSummary || null,
    healthScore: num(insights.health),
    healthVerdictText: insights.healthVeredict || '',
    takeaways: insights.takeaways || [],
    strengths: insights.strengths || [],
    concerns: insights.concerns || [],
    // Relevant facts that are not problems (cost concentration, thin buffer, document qualifiers), with figures
    observations: insights.observations || [],
    // Arithmetic checks of the most recent period, already labelled by Document Insights; null on older documents
    checks: doc?.checks || null,
    // Canonical lines ({ reported, derived, value, note } per line) and the normalizations behind them; null on
    // older documents, where the figures are read straight from insights
    canonical: doc?.canonical || null,
    normalizations: Array.isArray(doc?.normalizations) ? doc.normalizations : [],
    totalRevenue: optional(profitability.totalRevenue),
    costOfGoodsSold: optional(profitability.costOfGoodsSold),
    grossProfit: optional(profitability.grossProfit),
    grossMargin: optional(profitability.grossMargin),
    // A statement without any cost of sales has no gross margin to show (it is not 100%)
    grossMarginApplicable: isReported(profitability.costOfGoodsSold) || isReported(profitability.grossProfit),
    operatingExpenses: optional(profitability.operatingExpenses),
    operatingIncome,
    operatingMargin: optional(profitability.operatingMargin),
    netIncome,
    netMargin: optional(profitability.netMargin),
    depreciationAmortization: optional(cashEarnings.depreciationAmortization),
    ebitda: optional(cashEarnings.ebitda),
    ebitdaMargin: optional(cashEarnings.ebitdaMargin),
    interestExpense: optional(debtService.interestExpense),
    interestCoverage: optional(debtService.interestCoverage),
    cogsPct: optional(costStructure.cogsPercentOfRevenue),
    opexPct: optional(costStructure.operatingExpensesPercentOfRevenue),
    // Interest, taxes and other non-operating items, as the gap between operating and net income
    belowOperatingLine: isReported(operatingIncome) && isReported(netIncome) ? operatingIncome - netIncome : null
  }
}

// A printed figure counts as different from the derived one beyond half a unit of currency or 0.01% of the figure.
const REPORTED_TOLERANCE = 0.5
const REPORTED_TOLERANCE_RATIO = 0.0001

const normalizationText = (normalization) => [
  normalization.op,
  normalization.line,
  isReported(normalization.amount) ? formatCurrency(normalization.amount) : '',
  normalization.from || normalization.to ? `${normalization.from || NOT_REPORTED} → ${normalization.to || NOT_REPORTED}` : '',
  normalization.reason
].filter(Boolean).join(' · ')

// A normalization belongs to a line when it moves an amount into or out of it, or when the line's note names it.
const normalizationsOf = (metrics, lineName, note) => metrics.normalizations
  .filter((n) => n && (n.to === lineName || n.from === lineName || (!!note && !!n.line && note.includes(n.line))))
  .map(normalizationText)

// One canonical line of the document with what the UI needs to annotate its figure: the printed amount when the
// analysis uses a different one (statementReports), the note explaining why and the normalizations involved.
export const canonicalLine = (metrics, lineName) => {
  const line = metrics && metrics.canonical ? metrics.canonical[lineName] : null
  if (!line) return null
  const reported = optional(line.reported)
  const derived = optional(line.derived)
  const differs = isReported(reported) && isReported(derived)
    && Math.abs(reported - derived) > Math.max(REPORTED_TOLERANCE, Math.abs(derived) * REPORTED_TOLERANCE_RATIO)
  const note = line.note || ''
  return {
    name: lineName,
    value: optional(line.value),
    reported,
    derived,
    note,
    statementReports: differs ? reported : null,
    normalizations: normalizationsOf(metrics, lineName, note)
  }
}

// The headline is read off the same verdict as the health score, refined by the figures within that tier.
const pickHeadline = (metrics) => {
  if (!isReported(metrics.netIncome)) return strings.INCOME_STATEMENT_HEADLINE_INCOMPLETE
  if (metrics.netIncome < 0) return strings.INCOME_STATEMENT_HEADLINE_LOSS
  if (isReported(metrics.operatingIncome) && metrics.operatingIncome < 0) return strings.INCOME_STATEMENT_HEADLINE_OPERATING_LOSS
  const thin = isReported(metrics.netMargin) && metrics.netMargin < THRESHOLDS.NET_MARGIN_THIN
  const tier = getHealthStatusLabel(metrics.healthScore)
  if (tier === 'HEALTHY') return thin ? strings.INCOME_STATEMENT_HEADLINE_THIN : strings.INCOME_STATEMENT_HEADLINE_SOLID
  if (isReported(metrics.interestCoverage) && metrics.interestCoverage < THRESHOLDS.INTEREST_COVERAGE_FLOOR) {
    return strings.INCOME_STATEMENT_HEADLINE_DEBT_TIGHT
  }
  if (thin) return strings.INCOME_STATEMENT_HEADLINE_THIN
  return tier === 'WATCH' ? strings.INCOME_STATEMENT_HEADLINE_WATCH : strings.INCOME_STATEMENT_HEADLINE_AT_RISK
}

const marginNote = (margin, label, threshold) => {
  if (!isReported(margin)) return `${label} ${strings.INCOME_STATEMENT_NOT_REPORTED_SUFFIX}`
  const relation = margin < threshold ? strings.INCOME_STATEMENT_UNDER : strings.INCOME_STATEMENT_ABOVE
  return `${label} ${formatPercent(margin)} · ${relation} ${threshold}%`
}

export const deriveIncomeStatementVerdict = (doc, otherDoc) => {
  const metrics = readMetrics(doc)
  const statusLabel = getHealthStatusLabel(metrics.healthScore)
  const otherMetrics = otherDoc ? readMetrics(otherDoc) : null

  let revenueNote = isReported(metrics.cogsPct)
    ? `${strings.INCOME_STATEMENT_COGS_SHORT} ${formatPercent(metrics.cogsPct)} ${strings.INCOME_STATEMENT_OF_REVENUE}`
    : strings.INCOME_STATEMENT_NO_COST_BREAKDOWN
  if (otherMetrics && isReported(otherMetrics.totalRevenue) && otherMetrics.totalRevenue && isReported(metrics.totalRevenue)) {
    const change = ((metrics.totalRevenue - otherMetrics.totalRevenue) / Math.abs(otherMetrics.totalRevenue)) * 100
    if (Math.abs(change) >= 0.05) {
      revenueNote = `${formatSignedPercent(change)} ${strings.INCOME_STATEMENT_VS} ${formatDayLabel(otherMetrics.periodEnd)}`
    }
  }

  const ebitdaNote = isReported(metrics.ebitda)
    ? `${strings.INCOME_STATEMENT_EBITDA_MARGIN} ${formatOptionalPercent(metrics.ebitdaMargin)}`
    : strings.INCOME_STATEMENT_NO_DA_REPORTED

  return {
    metrics,
    statusLabel,
    eyebrow: `${formatPeriodRange(metrics.periodStart, metrics.periodEnd).toUpperCase()} · ${strings.INCOME_STATEMENT_VERDICT_LABEL}`,
    headline: pickHeadline(metrics),
    description: metrics.healthVerdictText || metrics.takeaways[0] || '',
    tileNotes: {
      revenue: revenueNote,
      grossProfit: marginNote(metrics.grossMargin, strings.INCOME_STATEMENT_GROSS_MARGIN, THRESHOLDS.GROSS_MARGIN),
      operatingIncome: marginNote(metrics.operatingMargin, strings.INCOME_STATEMENT_OPERATING_MARGIN, THRESHOLDS.OPERATING_MARGIN),
      netIncome: marginNote(metrics.netMargin, strings.INCOME_STATEMENT_NET_MARGIN, THRESHOLDS.NET_MARGIN_THIN),
      ebitda: ebitdaNote
    }
  }
}

// How the revenue of the period was spent: each segment is a share of total revenue, so a loss overflows the bar.
export const deriveComposition = (metrics) => {
  if (!isReported(metrics.totalRevenue) || metrics.totalRevenue <= 0) return null
  const revenue = metrics.totalRevenue
  const segments = [
    { key: 'cogs', label: strings.INCOME_STATEMENT_COGS_LABEL, value: metrics.costOfGoodsSold, color: ACCENT_ORANGE },
    { key: 'opex', label: strings.INCOME_STATEMENT_OPERATING_EXPENSES_LABEL, value: metrics.operatingExpenses, color: GRAY_BAR },
    { key: 'other', label: strings.INCOME_STATEMENT_BELOW_OPERATING_LABEL, value: metrics.belowOperatingLine, color: ACCENT_PURPLE },
    { key: 'net', label: strings.INCOME_STATEMENT_NET_INCOME_LABEL, value: metrics.netIncome, color: BANNER_BG }
  ].filter((segment) => isReported(segment.value))

  return {
    revenue,
    segments: segments.map((segment) => ({
      ...segment,
      sharePct: (segment.value / revenue) * 100,
      pct: Math.max(0, Math.min(100, (segment.value / revenue) * 100))
    }))
  }
}

export const deriveHealthIndicators = (metrics) => {
  const indicators = []

  if (isReported(metrics.grossMargin)) {
    const thin = metrics.grossMargin < THRESHOLDS.GROSS_MARGIN
    indicators.push({
      title: `${strings.INCOME_STATEMENT_GROSS_MARGIN} ${formatPercent(metrics.grossMargin)}`,
      subtext: thin
        ? `${strings.INCOME_STATEMENT_INDICATOR_BELOW_THE} ${THRESHOLDS.GROSS_MARGIN}% ${strings.INCOME_STATEMENT_THRESHOLD}`
        : `${strings.INCOME_STATEMENT_INDICATOR_AT_OR_ABOVE_THE} ${THRESHOLDS.GROSS_MARGIN}% ${strings.INCOME_STATEMENT_THRESHOLD}`,
      warning: thin
    })
  }

  if (isReported(metrics.operatingMargin)) {
    const weak = metrics.operatingMargin < THRESHOLDS.OPERATING_MARGIN
    indicators.push({
      title: `${strings.INCOME_STATEMENT_OPERATING_MARGIN} ${formatPercent(metrics.operatingMargin)}`,
      subtext: metrics.operatingMargin < 0
        ? strings.INCOME_STATEMENT_INDICATOR_OPERATING_LOSS
        : weak
          ? `${strings.INCOME_STATEMENT_INDICATOR_BELOW_THE} ${THRESHOLDS.OPERATING_MARGIN}% ${strings.INCOME_STATEMENT_THRESHOLD}`
          : `${strings.INCOME_STATEMENT_INDICATOR_AT_OR_ABOVE_THE} ${THRESHOLDS.OPERATING_MARGIN}% ${strings.INCOME_STATEMENT_THRESHOLD}`,
      warning: weak
    })
  }

  if (isReported(metrics.netIncome)) {
    const loss = metrics.netIncome < 0
    indicators.push({
      title: loss ? strings.INCOME_STATEMENT_INDICATOR_NET_LOSS : strings.INCOME_STATEMENT_INDICATOR_NET_INCOME_POSITIVE,
      subtext: `${formatSignedCurrency(metrics.netIncome)}, ${formatOptionalPercent(metrics.netMargin)} ${strings.INCOME_STATEMENT_OF_REVENUE}.`,
      warning: loss
    })
  }

  if (isReported(metrics.interestCoverage)) {
    const belowFloor = metrics.interestCoverage < THRESHOLDS.INTEREST_COVERAGE_FLOOR
    const belowComfort = metrics.interestCoverage < THRESHOLDS.INTEREST_COVERAGE_COMFORT
    indicators.push({
      title: `${strings.INCOME_STATEMENT_INTEREST_COVERAGE} ${formatMultiple(metrics.interestCoverage)}`,
      subtext: belowFloor
        ? strings.INCOME_STATEMENT_INDICATOR_COVERAGE_BELOW_FLOOR
        : belowComfort
          ? strings.INCOME_STATEMENT_INDICATOR_COVERAGE_BELOW_COMFORT
          : strings.INCOME_STATEMENT_INDICATOR_COVERAGE_HEALTHY,
      warning: belowComfort
    })
  } else if (!isReported(metrics.interestExpense)) {
    indicators.push({
      title: strings.INCOME_STATEMENT_INDICATOR_NO_INTEREST,
      subtext: strings.INCOME_STATEMENT_INDICATOR_NO_INTEREST_SUBTEXT,
      warning: false
    })
  }

  if (isReported(metrics.ebitda)) {
    indicators.push({
      title: `${strings.INCOME_STATEMENT_EBITDA} ${formatCurrency(metrics.ebitda)}`,
      subtext: `${formatOptionalPercent(metrics.ebitdaMargin)} ${strings.INCOME_STATEMENT_OF_REVENUE}, ${strings.INCOME_STATEMENT_INDICATOR_DA_ADDED_BACK} ${formatOptionalCurrency(metrics.depreciationAmortization)}.`,
      warning: metrics.ebitda < 0
    })
  }

  return indicators
}

const within = (a, b) => Math.abs(a - b) <= THRESHOLDS.CONSISTENCY_TOLERANCE

// Revenue − COGS must give the reported gross profit, and gross profit − operating expenses (− D&A when it is listed
// below the operating total) must give the reported operating income. Document Insights already consolidated the
// figures and reports its own warnings, which are shown next to this check.
export const deriveConsistencyCheck = (metrics) => {
  const hasGross = isReported(metrics.totalRevenue) && isReported(metrics.costOfGoodsSold) && isReported(metrics.grossProfit)
  const hasOperating = isReported(metrics.grossProfit) && isReported(metrics.operatingExpenses) && isReported(metrics.operatingIncome)
  if (!hasGross && !hasOperating) {
    return { reconciles: false, complete: false, text: strings.INCOME_STATEMENT_CHECK_INCOMPLETE }
  }

  const mismatches = []
  if (hasGross) {
    const expected = metrics.totalRevenue - metrics.costOfGoodsSold
    if (!within(expected, metrics.grossProfit)) {
      mismatches.push(`${strings.INCOME_STATEMENT_GROSS_PROFIT_LABEL} ${formatCurrency(metrics.grossProfit)} ${strings.INCOME_STATEMENT_VS} ${formatCurrency(expected)}`)
    }
  }
  if (hasOperating) {
    const beforeDa = metrics.grossProfit - metrics.operatingExpenses
    const afterDa = isReported(metrics.depreciationAmortization) ? beforeDa - metrics.depreciationAmortization : beforeDa
    if (!within(beforeDa, metrics.operatingIncome) && !within(afterDa, metrics.operatingIncome)) {
      mismatches.push(`${strings.INCOME_STATEMENT_OPERATING_INCOME_LABEL} ${formatCurrency(metrics.operatingIncome)} ${strings.INCOME_STATEMENT_VS} ${formatCurrency(beforeDa)}`)
    }
  }

  if (mismatches.length === 0) {
    return {
      reconciles: true,
      complete: hasGross && hasOperating,
      text: hasGross && hasOperating ? strings.INCOME_STATEMENT_CHECK_HOLDS : strings.INCOME_STATEMENT_CHECK_PARTIAL
    }
  }
  return { reconciles: false, complete: hasGross && hasOperating, text: `${strings.INCOME_STATEMENT_CHECK_FAILED} ${mismatches.join('; ')}.` }
}

export const deriveComparisonVerdict = (report, fromMetrics, toMetrics) => {
  const fromScore = fromMetrics.healthScore
  const toScore = toMetrics.healthScore
  const headline = toScore > fromScore
    ? strings.INCOME_STATEMENT_TREND_IMPROVING
    : toScore < fromScore
      ? strings.INCOME_STATEMENT_TREND_DETERIORATING
      : strings.INCOME_STATEMENT_TREND_STABLE
  const description = (report.takeawayAnalysis && report.takeawayAnalysis[0]) || ''
  return {
    headline,
    description,
    fromScore,
    toScore,
    improved: toScore >= fromScore,
    comparable: report.periodsComparable !== false,
    comparabilityNote: report.comparabilityNote || ''
  }
}

// Reported changes win; when Document Insights could not compute one (a figure missing in either period) it is null.
export const changeOf = (report, key, fromValue, toValue) => {
  const reported = report ? report[key] : null
  if (reported && isReported(reported.absolute_delta)) {
    return {
      absolute: Number(reported.absolute_delta),
      percent: isReported(reported.percentage_change) ? Number(reported.percentage_change) : null
    }
  }
  if (!isReported(fromValue) || !isReported(toValue)) return null
  const absolute = toValue - fromValue
  return { absolute, percent: fromValue ? (absolute / Math.abs(fromValue)) * 100 : null }
}

// --- Report layout (after the "Income statement analysis" design reference) ---

// Severity chips reuse the Balance Sheet status colors: orange for warnings, purple for watch items, green for positives.
export const SEVERITY_STYLES = {
  High: { bg: 'rgba(232,115,74,0.16)', fg: 'var(--status-pending)' },
  Medium: { bg: 'rgba(155,138,251,0.18)', fg: '#7a66f0' },
  Low: { bg: '#F1F1F1', fg: '#555' },
  Info: { bg: '#F1F1F1', fg: '#555' },
  Positive: { bg: 'rgba(76,175,130,0.16)', fg: '#2c8f68' },
  // Model narrative: a concern reads as a warning, an observation as a watch item
  Concern: { bg: 'rgba(232,115,74,0.16)', fg: 'var(--status-pending)' },
  Observation: { bg: 'rgba(155,138,251,0.18)', fg: '#7a66f0' }
}

export const CHECK_STATUS = {
  PASS: 'PASS',
  DERIVED: 'DERIVED',
  DOUBT: 'DOUBT',
  WARNING: 'WARNING'
}

const parseDay = (dateStr) => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateStr || '')
  return match ? new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3])) : null
}

export const periodDays = (periodStart, periodEnd) => {
  const start = parseDay(periodStart)
  const end = parseDay(periodEnd)
  if (!start || !end || end < start) return null
  return Math.round((end - start) / 86400000) + 1
}

// Whole months when the period runs from the first day of a month to the last day of another (a fiscal year is 12);
// otherwise an estimate from the day count, flagged so the UI can say which formula was used.
export const periodMonths = (periodStart, periodEnd) => {
  const start = parseDay(periodStart)
  const end = parseDay(periodEnd)
  if (!start || !end || end < start) return null
  const startsOnFirst = start.getDate() === 1
  const endsOnLast = new Date(end.getFullYear(), end.getMonth() + 1, 0).getDate() === end.getDate()
  if (startsOnFirst && endsOnLast) {
    return { months: (end.getFullYear() - start.getFullYear()) * 12 + end.getMonth() - start.getMonth() + 1, exact: true }
  }
  return { months: (Math.round((end - start) / 86400000) + 1) / (365.25 / 12), exact: false }
}

export const formatCompactCurrency = (value) => {
  if (!isReported(value)) return NOT_REPORTED
  const abs = Math.abs(value)
  const sign = value < 0 ? '-' : ''
  if (abs >= 1e6) return `${sign}$${(abs / 1e6).toFixed(abs >= 1e7 ? 0 : 1)}M`
  if (abs >= 1e3) return `${sign}$${(abs / 1e3).toFixed(abs >= 1e5 ? 0 : 1)}k`
  return `${sign}$${abs.toFixed(0)}`
}

const tierText = (statusLabel) => {
  if (statusLabel === 'HEALTHY') return strings.INCOME_STATEMENT_TIER_HEALTHY
  if (statusLabel === 'WATCH') return strings.INCOME_STATEMENT_TIER_WATCH
  return strings.INCOME_STATEMENT_TIER_AT_RISK
}

// Health card: tier plus trend against the other period, headline, narrative and up to three bullets.
export const deriveSummary = (metrics, otherMetrics) => {
  const statusLabel = getHealthStatusLabel(metrics.healthScore)
  let trend = ''
  if (otherMetrics && isReported(otherMetrics.healthScore) && otherMetrics.date && otherMetrics.date < metrics.date) {
    if (metrics.healthScore > otherMetrics.healthScore) trend = strings.INCOME_STATEMENT_TREND_IMPROVING.toLowerCase()
    else if (metrics.healthScore < otherMetrics.healthScore) trend = strings.INCOME_STATEMENT_TREND_DETERIORATING.toLowerCase()
    else trend = strings.INCOME_STATEMENT_TREND_STABLE.toLowerCase()
  }
  const takeaways = metrics.takeaways || []
  return {
    statusLabel,
    statusText: trend ? `${tierText(statusLabel)}, ${trend}` : tierText(statusLabel),
    headline: pickHeadline(metrics),
    narrative: takeaways[0] || metrics.healthVerdictText || '',
    bullets: takeaways.slice(1, 4)
  }
}

const deltaText = (current, previous, formatter, asMultiple) => {
  if (!isReported(current) || !isReported(previous)) return ''
  if (asMultiple) {
    if (current === previous) return strings.INCOME_STATEMENT_KPI_UNCHANGED
    return `${current > previous ? strings.INCOME_STATEMENT_KPI_UP_FROM : strings.INCOME_STATEMENT_KPI_DOWN_FROM} ${formatter(previous)}`
  }
  if (!previous) return ''
  const change = ((current - previous) / Math.abs(previous)) * 100
  return `${formatSignedPercent(change)} ${strings.INCOME_STATEMENT_KPI_VS_PREVIOUS}`
}

const bars = (current, previous, formatter) => {
  if (!isReported(current) || !previous || !isReported(previous.value)) return []
  const scale = Math.max(Math.abs(current), Math.abs(previous.value), 1)
  return [
    { key: 'previous', label: previous.label, text: formatter(previous.value), pct: (Math.abs(previous.value) / scale) * 100, negative: previous.value < 0, last: false },
    { key: 'current', label: strings.INCOME_STATEMENT_CURRENT, text: formatter(current), pct: (Math.abs(current) / scale) * 100, negative: current < 0, last: true }
  ]
}

// Four cards: revenue, EBITDA (or operating income when D&A is not reported), net income and interest coverage.
export const deriveKeyIndicators = (metrics, otherMetrics) => {
  const previous = (value) => (otherMetrics ? { label: strings.INCOME_STATEMENT_PREVIOUS, value } : null)
  const cards = []

  // With a canonical model the gross margin is only quoted when the model itself carries a gross profit
  const grossProfitLine = canonicalLine(metrics, 'grossProfit')
  const grossMarginShown = isReported(metrics.grossMargin) && (!metrics.canonical || (!!grossProfitLine && isReported(grossProfitLine.value)))
  cards.push({
    key: 'revenue',
    name: strings.INCOME_STATEMENT_KPI_REVENUE,
    value: formatOptionalCurrency(metrics.totalRevenue),
    sub: grossMarginShown
      ? `${strings.INCOME_STATEMENT_GROSS_MARGIN} ${formatShare(metrics.grossMargin)}, ${strings.INCOME_STATEMENT_COGS_SHORT} ${formatOptionalShare(metrics.cogsPct)} ${strings.INCOME_STATEMENT_OF_REVENUE}`
      : metrics.grossMarginApplicable && !metrics.canonical ? strings.INCOME_STATEMENT_NO_COST_BREAKDOWN : strings.INCOME_STATEMENT_GROSS_MARGIN_NA,
    delta: deltaText(metrics.totalRevenue, otherMetrics && otherMetrics.totalRevenue, formatCompactCurrency, false),
    bars: bars(metrics.totalRevenue, previous(otherMetrics && otherMetrics.totalRevenue), formatCompactCurrency),
    line: canonicalLine(metrics, 'revenue')
  })

  if (isReported(metrics.ebitda)) {
    cards.push({
      key: 'ebitda',
      name: strings.INCOME_STATEMENT_EBITDA,
      value: formatCurrency(metrics.ebitda),
      sub: `${formatOptionalShare(metrics.ebitdaMargin)} ${strings.INCOME_STATEMENT_KPI_MARGIN}, ${strings.INCOME_STATEMENT_INDICATOR_DA_ADDED_BACK} ${formatOptionalCurrency(metrics.depreciationAmortization)}`,
      delta: deltaText(metrics.ebitda, otherMetrics && otherMetrics.ebitda, formatCompactCurrency, false),
      bars: bars(metrics.ebitda, previous(otherMetrics && otherMetrics.ebitda), formatCompactCurrency),
      line: canonicalLine(metrics, 'ebitda')
    })
  } else {
    cards.push({
      key: 'operatingIncome',
      name: strings.INCOME_STATEMENT_OPERATING_INCOME_LABEL,
      value: formatOptionalCurrency(metrics.operatingIncome),
      sub: isReported(metrics.operatingMargin)
        ? `${formatShare(metrics.operatingMargin)} ${strings.INCOME_STATEMENT_KPI_MARGIN}. ${strings.INCOME_STATEMENT_NO_DA_REPORTED}`
        : strings.INCOME_STATEMENT_NO_DA_REPORTED,
      delta: deltaText(metrics.operatingIncome, otherMetrics && otherMetrics.operatingIncome, formatCompactCurrency, false),
      bars: bars(metrics.operatingIncome, previous(otherMetrics && otherMetrics.operatingIncome), formatCompactCurrency),
      line: canonicalLine(metrics, 'operatingIncome')
    })
  }

  cards.push({
    key: 'netIncome',
    name: strings.INCOME_STATEMENT_NET_INCOME_LABEL,
    value: formatOptionalCurrency(metrics.netIncome),
    sub: isReported(metrics.netMargin)
      ? `${formatShare(metrics.netMargin)} ${strings.INCOME_STATEMENT_KPI_NET_MARGIN_SUB}`
      : strings.INCOME_STATEMENT_HEADLINE_INCOMPLETE,
    delta: deltaText(metrics.netIncome, otherMetrics && otherMetrics.netIncome, formatCompactCurrency, false),
    bars: bars(metrics.netIncome, previous(otherMetrics && otherMetrics.netIncome), formatCompactCurrency),
    negative: isReported(metrics.netIncome) && metrics.netIncome < 0,
    line: canonicalLine(metrics, 'netIncome')
  })

  cards.push({
    key: 'interestCoverage',
    name: strings.INCOME_STATEMENT_INTEREST_COVERAGE,
    value: formatOptionalMultiple(metrics.interestCoverage),
    sub: isReported(metrics.interestCoverage)
      ? `${strings.INCOME_STATEMENT_KPI_COVERAGE_SUB} ${formatOptionalCurrency(metrics.interestExpense)}`
      : strings.INCOME_STATEMENT_INDICATOR_NO_INTEREST_SUBTEXT,
    delta: deltaText(metrics.interestCoverage, otherMetrics && otherMetrics.interestCoverage, formatMultiple, true),
    bars: bars(metrics.interestCoverage, previous(otherMetrics && otherMetrics.interestCoverage), formatMultiple),
    negative: isReported(metrics.interestCoverage) && metrics.interestCoverage < THRESHOLDS.INTEREST_COVERAGE_FLOOR
  })

  return cards
}

// Cost rows read from the canonical model: one row per line the document carries, costs first and the income lines
// (other income, interest income) last as positive amounts. No row is invented for a line the model left null.
const canonicalCostRows = (metrics) => {
  const row = (key, lineName, label, type, extra = {}) => {
    const line = canonicalLine(metrics, lineName)
    return { key, label, type, value: line ? line.value : null, line, note: '', ...extra }
  }
  return [
    row('cogs', 'cogs', strings.INCOME_STATEMENT_COGS_LABEL, strings.INCOME_STATEMENT_COST_TYPE_DIRECT),
    row('opex', 'operatingExpensesExDA', strings.INCOME_STATEMENT_OPERATING_EXPENSES_LABEL, strings.INCOME_STATEMENT_COST_TYPE_OPERATING),
    row('da', 'depreciationAmortization', strings.INCOME_STATEMENT_COST_DA, strings.INCOME_STATEMENT_COST_TYPE_OPERATING),
    row('interest', 'interestExpense', strings.INCOME_STATEMENT_COST_INTEREST, strings.INCOME_STATEMENT_COST_TYPE_DEBT, { debt: true }),
    row('otherExpense', 'otherExpense', strings.INCOME_STATEMENT_COST_OTHER_EXPENSE, strings.INCOME_STATEMENT_COST_TYPE_OTHER),
    row('incomeTax', 'incomeTax', strings.INCOME_STATEMENT_COST_INCOME_TAX, strings.INCOME_STATEMENT_COST_TYPE_OTHER),
    row('otherIncome', 'otherIncome', strings.INCOME_STATEMENT_COST_OTHER_INCOME, strings.INCOME_STATEMENT_COST_TYPE_INCOME, { income: true }),
    row('interestIncome', 'interestIncome', strings.INCOME_STATEMENT_COST_INTEREST_INCOME, strings.INCOME_STATEMENT_COST_TYPE_INCOME, { income: true })
  ]
}

// Cost rows of an older document, read from the insights: interest, taxes and other below the operating line are
// split when possible.
const legacyCostRows = (metrics) => {
  const taxesAndOther = isReported(metrics.belowOperatingLine) && isReported(metrics.interestExpense)
    ? metrics.belowOperatingLine - metrics.interestExpense
    : null
  return [
    { key: 'cogs', label: strings.INCOME_STATEMENT_COGS_LABEL, type: strings.INCOME_STATEMENT_COST_TYPE_DIRECT, value: metrics.costOfGoodsSold },
    { key: 'opex', label: strings.INCOME_STATEMENT_OPERATING_EXPENSES_LABEL, type: strings.INCOME_STATEMENT_COST_TYPE_OPERATING, value: metrics.operatingExpenses, note: isReported(metrics.depreciationAmortization) ? `${strings.INCOME_STATEMENT_COST_DA_NOTE} ${formatCurrency(metrics.depreciationAmortization)}` : '' },
    { key: 'interest', label: strings.INCOME_STATEMENT_COST_INTEREST, type: strings.INCOME_STATEMENT_COST_TYPE_DEBT, value: metrics.interestExpense, debt: true },
    { key: 'other', label: strings.INCOME_STATEMENT_COST_TAXES_OTHER, type: strings.INCOME_STATEMENT_COST_TYPE_OTHER, value: taxesAndOther }
  ].map((row) => ({ line: null, note: '', income: false, ...row }))
}

// Costs of the period as a share of revenue. With a canonical model the rows are its lines and the total adds up
// the cost rows only (the income rows are listed, not deducted); otherwise the total is revenue − net income.
export const deriveCostStructure = (metrics) => {
  if (!isReported(metrics.totalRevenue) || metrics.totalRevenue <= 0) return null
  const period = periodMonths(metrics.periodStart, metrics.periodEnd)
  const months = period ? period.months : null
  const monthlyFormula = !period ? ''
    : period.exact
      ? `${strings.INCOME_STATEMENT_COST_MONTHLY_FORMULA_EXACT} ${period.months}`
      : strings.INCOME_STATEMENT_COST_MONTHLY_FORMULA_DAYS
  const revenue = metrics.totalRevenue
  const rows = (metrics.canonical ? canonicalCostRows(metrics) : legacyCostRows(metrics))
    .filter((row) => isReported(row.value))
    .map((row) => ({
      ...row,
      monthly: months ? row.value / months : null,
      pct: (row.value / revenue) * 100
    }))
  const costRows = rows.filter((row) => !row.income)
  const totalCosts = !metrics.canonical && isReported(metrics.netIncome)
    ? revenue - metrics.netIncome
    : costRows.reduce((sum, row) => sum + row.value, 0)
  return {
    rows,
    months,
    monthlyFormula,
    total: { value: totalCosts, monthly: months ? totalCosts / months : null, pct: (totalCosts / revenue) * 100 },
    periodLabel: formatPeriodRange(metrics.periodStart, metrics.periodEnd)
  }
}

// Only what the model wrote, concerns first, then observations, then strengths. The computed signals
// (deriveHealthIndicators) no longer share this card: the checks card is where the figures are verified.
export const deriveFlags = (metrics) => [
  ...metrics.concerns.map((text) => ({ severity: 'Concern', label: strings.INCOME_STATEMENT_SEVERITY_CONCERN, line: text })),
  ...metrics.observations.map((text) => ({ severity: 'Observation', label: strings.INCOME_STATEMENT_SEVERITY_OBSERVATION, line: text })),
  ...metrics.strengths.map((text) => ({ severity: 'Positive', label: strings.INCOME_STATEMENT_SEVERITY_POSITIVE, line: text }))
]

const withinPoints = (a, b, tolerance) => Math.abs(a - b) <= tolerance

// Arithmetic checks on the consolidated figures plus the warnings Document Insights raised while reading the pages.
export const deriveExtractionChecks = (metrics) => {
  const checks = []
  const add = (label, pass) => checks.push({ label, pass, warn: !pass })
  const reported = (...values) => values.every(isReported)

  if (reported(metrics.totalRevenue, metrics.costOfGoodsSold, metrics.grossProfit)) {
    add(strings.INCOME_STATEMENT_CHECK_GROSS, within(metrics.totalRevenue - metrics.costOfGoodsSold, metrics.grossProfit))
  }
  // Without cost of sales the operating income is checked straight from revenue
  const operatingBase = isReported(metrics.grossProfit) ? metrics.grossProfit : (!metrics.grossMarginApplicable ? metrics.totalRevenue : null)
  if (reported(operatingBase, metrics.operatingExpenses, metrics.operatingIncome)) {
    const beforeDa = operatingBase - metrics.operatingExpenses
    const afterDa = isReported(metrics.depreciationAmortization) ? beforeDa - metrics.depreciationAmortization : beforeDa
    add(isReported(metrics.grossProfit) ? strings.INCOME_STATEMENT_CHECK_OPERATING : strings.INCOME_STATEMENT_CHECK_OPERATING_NO_COGS,
      within(beforeDa, metrics.operatingIncome) || within(afterDa, metrics.operatingIncome))
  }
  if (reported(metrics.operatingIncome, metrics.depreciationAmortization, metrics.ebitda)) {
    add(strings.INCOME_STATEMENT_CHECK_EBITDA, within(metrics.operatingIncome + metrics.depreciationAmortization, metrics.ebitda))
  }
  if (reported(metrics.totalRevenue, metrics.netIncome, metrics.netMargin) && metrics.totalRevenue) {
    const grossOk = !reported(metrics.grossProfit, metrics.grossMargin)
      || withinPoints((metrics.grossProfit / metrics.totalRevenue) * 100, metrics.grossMargin, 0.1)
    const netOk = withinPoints((metrics.netIncome / metrics.totalRevenue) * 100, metrics.netMargin, 0.1)
    add(strings.INCOME_STATEMENT_CHECK_MARGINS, grossOk && netOk)
  }
  if (reported(metrics.operatingIncome, metrics.interestExpense) && isReported(metrics.interestCoverage) && metrics.interestExpense > 0) {
    add(strings.INCOME_STATEMENT_CHECK_COVERAGE, withinPoints(metrics.operatingIncome / metrics.interestExpense, metrics.interestCoverage, 0.01))
  }
  // Only figures the statement should carry count as missing: gross profit does not exist without cost of sales
  const expected = ['totalRevenue', 'operatingExpenses', 'operatingIncome', 'netIncome']
    .concat(metrics.grossMarginApplicable ? ['costOfGoodsSold', 'grossProfit'] : [])
  const missing = expected.filter((key) => !isReported(metrics[key]))
  if (missing.length > 0) {
    checks.push({ label: `${strings.INCOME_STATEMENT_CHECK_MISSING} ${missing.length}`, pass: false, warn: true })
  }
  metrics.warnings.forEach((warning) => checks.push({ label: warning, pass: false, warn: true }))
  // Doubts are shown as information: the printed figure was kept and nothing about the business follows from them
  metrics.extractionDoubts.forEach((doubt) => checks.push({ label: `${strings.INCOME_STATEMENT_CHECK_UNVERIFIED_PREFIX} ${doubt}`, pass: false, warn: false, info: true }))
  if (checks.length === 0) {
    checks.push({ label: strings.INCOME_STATEMENT_CHECK_INCOMPLETE, pass: false, warn: true })
  }
  return checks
}

// A fallback check keeps the same shape as one from Document Insights so the card renders either without caring.
const fallbackCheckStatus = (check) => {
  if (check.pass) return CHECK_STATUS.PASS
  return check.info ? CHECK_STATUS.DOUBT : CHECK_STATUS.WARNING
}

// Checks card: the checks Document Insights ran on the most recent period, labelled by the backend (the label is
// never rewritten here), followed by the warnings and extraction doubts that are not already the note of a check.
// Older documents carry no checks, so the identities are derived in the client instead (deriveExtractionChecks).
export const deriveChecks = (metrics) => {
  if (!Array.isArray(metrics.checks)) {
    return deriveExtractionChecks(metrics).map((check, index) => ({
      key: `fallback-${index}`,
      label: check.label,
      status: fallbackCheckStatus(check),
      verified: !!check.pass,
      note: '',
      tolerance: null,
      fallback: true
    }))
  }

  const items = metrics.checks.map((check, index) => ({
    key: check.id || check.name || `check-${index}`,
    label: check.label || check.formula || '',
    status: CHECK_STATUS[check.status] || CHECK_STATUS.WARNING,
    // Only a check that compared against a printed figure verifies anything; a derived one is shown in gray.
    // Older documents carry no flag: there, anything but a derived check compared against the statement.
    verified: check.verified === true || (check.verified === undefined && check.status !== CHECK_STATUS.DERIVED),
    note: check.note || '',
    tolerance: optional(check.tolerance),
    fallback: false
  }))
  const notes = new Set(items.map((item) => item.note).filter(Boolean))
  metrics.warnings
    .filter((text) => !notes.has(text))
    .forEach((text, index) => items.push({ key: `warning-${index}`, label: text, status: CHECK_STATUS.WARNING, verified: true, note: '', tolerance: null, fallback: false }))
  metrics.extractionDoubts
    .filter((text) => !notes.has(text))
    .forEach((text, index) => items.push({ key: `doubt-${index}`, label: text, status: CHECK_STATUS.DOUBT, verified: false, note: '', tolerance: null, fallback: false }))
  return items
}

// The rail chip: what Document Insights says the checks verified. An incomplete statement is never green; a
// document without a summary (older analyses) falls back to the warning and doubt counts.
export const deriveChecksChip = (metrics) => {
  if (!metrics.statementComplete) return { tone: 'High', label: strings.INCOME_STATEMENT_CHIP_INCOMPLETE }
  const summary = metrics.checksSummary
  if (summary && summary.label) {
    const green = summary.allKeyLinesVerified && summary.allPassed
    return { tone: green ? 'Positive' : (summary.failed > 0 ? 'High' : 'Medium'), label: summary.label }
  }
  const warningCount = metrics.warnings.length
  const doubtCount = metrics.extractionDoubts.length
  if (warningCount > 0) {
    return { tone: 'High', label: `${warningCount} ${warningCount === 1 ? strings.INCOME_STATEMENT_CHIP_WARNING_SUFFIX : strings.INCOME_STATEMENT_CHIP_WARNINGS_SUFFIX}` }
  }
  if (doubtCount > 0) {
    return { tone: 'Medium', label: `${doubtCount} ${doubtCount === 1 ? strings.INCOME_STATEMENT_CHIP_UNVERIFIED_SINGULAR : strings.INCOME_STATEMENT_CHIP_UNVERIFIED_SUFFIX}` }
  }
  return { tone: 'Positive', label: strings.INCOME_STATEMENT_CHIP_CHECKS_PASSED }
}

// What to tell the user when Document Insights rejected the upload: the type it recognized, then its own message.
export const rejectionText = (metrics) => {
  if (metrics.validIncomeStatement) return ''
  return metrics.error || strings.INCOME_STATEMENT_ANALYSIS_FAILED
}
